import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';
import _debounce from 'lodash/debounce';
import DataTable from 'react-data-table-component';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import QRCode from 'qrcode';

import '../../../App.css';

import {
  useGetContactUsQuery,
  useEditContactUsMutation,
  useDeleteContactUsMutation
} from '../../../redux/apis/ContactUsApi';

import { sharedTableStyles } from '../../../utils/tableStyles';
import { TableSkeleton } from '../../../components/UI/Skeleton/Skeleton';
import RowActionMenu from '../../../components/UI/RowActionMenu/RowActionMenu';
import TableCard from '../../../components/UI/TableToolbar/TableCard';
import TableToolbar from '../../../components/UI/TableToolbar/TableToolbar';
import EmptyData from '../../../components/UI/EmptyData';
import PageHeader from '../../../components/common/PageHeader';
import ConfirmDialog from '../../../components/common/ConfirmDialog';

import { notification } from '../../../utils/notification';
import { TOAST_TYPE } from '../../../utils/constant/errorMessage';
import { skipRow, paginationRow, formatDate } from '../../../utils/handler';

import './ContactUsList.scss';

const { TOAST_SUCCESS, TOAST_ERROR } = TOAST_TYPE;

// Public Guest Check-In form URL that the QR code points to (same app origin).
const CHECKIN_FORM_URL = `${window.location.origin}/contact-us`;

const VISIT_STATUS = {
  PENDING: 'Pending',
  CHECKED_IN: 'CheckedIn',
  CHECKED_OUT: 'CheckedOut'
};

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: VISIT_STATUS.PENDING, label: 'Pending' },
  { value: VISIT_STATUS.CHECKED_IN, label: 'Checked In' },
  { value: VISIT_STATUS.CHECKED_OUT, label: 'Checked Out' }
];

const STATUS_LABEL = {
  [VISIT_STATUS.PENDING]: 'Pending',
  [VISIT_STATUS.CHECKED_IN]: 'Checked In',
  [VISIT_STATUS.CHECKED_OUT]: 'Checked Out'
};

const statusBadge = (status) => (
  <span className={`guest-status guest-status--${status || 'unknown'}`}>
    {STATUS_LABEL[status] || status || '-'}
  </span>
);

const yesNo = (v) => (v === true ? 'Yes' : v === false ? 'No' : '-');

const initials = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((n) => n[0].toUpperCase())
    .slice(0, 2)
    .join('') || 'V';

const YesNoChip = ({ value }) =>
  value === true ? (
    <span className="vd-modal__chip vd-modal__chip--yes">
      <i className="icofont-check-circled" aria-hidden="true" />
      Yes
    </span>
  ) : value === false ? (
    <span className="vd-modal__chip vd-modal__chip--no">
      <i className="icofont-close-circled" aria-hidden="true" />
      No
    </span>
  ) : (
    <span className="vd-modal__item-value">-</span>
  );

const initialState = {
  search: '',
  statusFilter: '',
  currentPage: 1,
  rowsPerPage: 10,
  isNext: false,
  sortField: 'createdAt',
  sortDirection: false,
  viewModal: { status: false, data: {} },
  deleteModal: { status: false, id: '' },
  deleting: false,
  qrModal: false,
  qrDataUrl: ''
};

const guestTableStyles = {
  ...sharedTableStyles,
  headCells: {
    style: {
      ...sharedTableStyles.headCells.style,
      color: '#334155',
      fontWeight: 700
    }
  },
  rows: {
    ...sharedTableStyles.rows,
    style: {
      ...sharedTableStyles.rows.style,
      color: '#6b7280'
    }
  },
  cells: {
    style: {
      ...sharedTableStyles.cells.style,
      color: '#6b7280',
      fontWeight: 600
    }
  }
};

const ContactUsList = () => {
  const [state, setState] = useState(initialState);
  const qrGeneratedRef = useRef(false);

  const { data, isLoading, isFetching, refetch } = useGetContactUsQuery({
    search: state.search,
    status: state.statusFilter,
    skip: skipRow(state.currentPage, state.rowsPerPage),
    limit: state.rowsPerPage,
    sort: state.sortField,
    sortType: state.sortDirection ? 'desc' : 'asc'
  });

  const [editContactUs] = useEditContactUsMutation();
  const [deleteContactUs] = useDeleteContactUsMutation();

  useEffect(() => {
    refetch();
    setState((prev) => ({ ...prev, isNext: data?.isNext }));
  }, [state.rowsPerPage, state.currentPage, state.search, state.statusFilter, state.sortDirection]);

  const handleChangeSort = (sortField) => {
    setState((prev) => ({
      ...prev,
      sortDirection: !prev.sortDirection,
      sortField: sortField.sortField
    }));
  };

  const handleChangePage = (page) => setState((prev) => ({ ...prev, currentPage: page }));

  const handleChangeRowsPerPage = (newRowsPerPage) =>
    setState((prev) => ({ ...prev, rowsPerPage: newRowsPerPage, currentPage: 1 }));

  const debouncedOnSearch = _debounce((value) => {
    setState((prev) => ({ ...prev, search: value, currentPage: 1 }));
  }, 300);

  const handleStatusChange = async (id, status) => {
    try {
      const res = await editContactUs({ id, data: { status } });
      if (res?.data?.success) {
        notification(res?.data?.statusMessage, TOAST_SUCCESS);
        refetch();
      } else {
        notification(res?.error?.data?.statusMessage || 'Failed to update', TOAST_ERROR);
      }
    } catch (error) {
      notification(error?.message, TOAST_ERROR);
    }
  };

  const handleView = (row) => {
    setState((prev) => ({ ...prev, viewModal: { status: true, data: row } }));
  };

  const handleDelete = async () => {
    try {
      setState((prev) => ({ ...prev, deleting: true }));
      const res = await deleteContactUs(state.deleteModal.id);
      if (res?.data?.success) {
        notification(res?.data?.statusMessage, TOAST_SUCCESS);
        refetch();
      } else {
        notification(res?.error?.data?.statusMessage || 'Failed to delete', TOAST_ERROR);
      }
    } catch (error) {
      notification(error?.message, TOAST_ERROR);
    } finally {
      setState((prev) => ({ ...prev, deleting: false, deleteModal: { status: false, id: '' } }));
    }
  };

  const handleExport = () => {
    const rows = data?.data || [];
    if (!rows.length) {
      notification('No data to export', TOAST_ERROR);
      return;
    }
    const payload = rows.map((row) => ({
      'Visitor Name': row?.name || '',
      Mobile: row?.phone || '',
      Email: row?.email || '',
      Company: row?.company || '',
      'Host (Meeting)': row?.hostName || '',
      Purpose: row?.purpose || '',
      Appointment: yesNo(row?.hasAppointment),
      'Expected Time': row?.expectedMeetingTime || '',
      Accessibility: yesNo(row?.needsAccessibility),
      'Accessibility Details': row?.accessibilityDetails || '',
      Status: STATUS_LABEL[row?.status] || row?.status || '',
      'Checked In On': formatDate(row?.createdAt, 'DD-MMM-YYYY hh:mm a')
    }));

    const worksheet = XLSX.utils.json_to_sheet(payload);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Visitors');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, 'GuestCheckIns.xlsx');
  };

  const openQrModal = async () => {
    setState((prev) => ({ ...prev, qrModal: true }));
    if (qrGeneratedRef.current) return;
    try {
      const dataUrl = await QRCode.toDataURL(CHECKIN_FORM_URL, {
        width: 320,
        margin: 2,
        color: { dark: '#484c7f', light: '#ffffff' }
      });
      qrGeneratedRef.current = true;
      setState((prev) => ({ ...prev, qrDataUrl: dataUrl }));
    } catch (error) {
      notification('Failed to generate QR code', TOAST_ERROR);
    }
  };

  const handleDownloadQr = () => {
    if (!state.qrDataUrl) return;
    saveAs(state.qrDataUrl, 'GuestCheckInQRCode.png');
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(CHECKIN_FORM_URL);
      notification('Link copied to clipboard', TOAST_SUCCESS);
    } catch (error) {
      notification('Could not copy the link', TOAST_ERROR);
    }
  };

  const columns = [
    {
      name: 'Visitor',
      sortField: 'name',
      sortable: true,
      selector: (row) => row?.name || '-',
      cell: (row) => (
        <div className="guest-visitor">
          <span className="guest-visitor__avatar" aria-hidden="true">
            {initials(row?.name)}
          </span>
          <div className="guest-visitor__copy">
            <span className="guest-visitor__name">{row?.name || '-'}</span>
            {row?.company ? <span className="guest-visitor__company">{row.company}</span> : null}
          </div>
        </div>
      )
    },
    {
      name: 'Mobile',
      selector: (row) => row?.phone || '-'
    },
    {
      name: 'Host',
      selector: (row) => row?.hostName || '-'
    },
    {
      name: 'Purpose',
      selector: (row) => row?.purpose || '-'
    },
    {
      name: 'Appt.',
      selector: (row) => yesNo(row?.hasAppointment),
      width: '110px',
      cell: (row) => (
        <span
          className={`guest-appt${
            row?.hasAppointment === true ? ' is-yes' : row?.hasAppointment === false ? ' is-no' : ''
          }`}
        >
          {yesNo(row?.hasAppointment)}
        </span>
      )
    },
    {
      name: 'Checked In On',
      sortField: 'createdAt',
      sortable: true,
      selector: (row) => formatDate(row?.createdAt, 'DD-MMM-YYYY hh:mm a')
    },
    {
      name: 'Status',
      selector: (row) => statusBadge(row?.status)
    },
    {
      name: 'Action',
      selector: () => {},
      cell: (row) => (
        <RowActionMenu
          actions={[
            {
              label: 'View',
              icon: 'icofont-eye-alt',
              onClick: () => handleView(row)
            },
            {
              label: 'Mark Checked In',
              icon: 'icofont-check',
              variant: 'success',
              hidden: row?.status !== VISIT_STATUS.PENDING,
              onClick: () => handleStatusChange(row._id, VISIT_STATUS.CHECKED_IN)
            },
            {
              label: 'Mark Checked Out',
              icon: 'icofont-sign-out',
              hidden: row?.status !== VISIT_STATUS.CHECKED_IN,
              onClick: () => handleStatusChange(row._id, VISIT_STATUS.CHECKED_OUT)
            },
            {
              label: 'Delete',
              icon: 'icofont-ui-delete',
              variant: 'danger',
              onClick: () =>
                setState((prev) => ({ ...prev, deleteModal: { status: true, id: row._id } }))
            }
          ]}
        />
      )
    }
  ];

  const viewData = state.viewModal.data || {};

  return (
    <div className="container-xxl guest-checkin">
      <PageHeader headerTitle="Guest Check-In" />
      <div className="row clearfix g-3">
        <div className="col-sm-12">
          <TableCard>
            <TableToolbar
              onSearch={debouncedOnSearch}
              filters={
                <div className="guest-status-filter">
                  <Select
                    classNamePrefix="guest-status-select"
                    options={STATUS_OPTIONS}
                    value={STATUS_OPTIONS.find((o) => o.value === state.statusFilter)}
                    onChange={(opt) =>
                      setState((prev) => ({ ...prev, statusFilter: opt.value, currentPage: 1 }))
                    }
                    placeholder="Filter by status"
                    isSearchable={false}
                  />
                </div>
              }
              actions={
                <div className="guest-toolbar__actions">
                  <button type="button" className="guest-toolbar__ghost" onClick={handleExport}>
                    <i className="icofont-download" aria-hidden="true" />
                    Export
                  </button>
                  <button
                    type="button"
                    className="app-table-toolbar__add-btn"
                    onClick={openQrModal}
                  >
                    <i className="icofont-qr-code" aria-hidden="true" />
                    QR Code
                  </button>
                </div>
              }
            />
            <div className="card-body">
              <div className="guest-checkin-table">
                <DataTable
                  customStyles={guestTableStyles}
                  columns={columns}
                  data={data?.data || []}
                  pagination
                  paginationServer
                  onSort={handleChangeSort}
                  sortField={state.sortField}
                  sortDirection={state.sortDirection}
                  paginationTotalRows={paginationRow(
                    data?.isNext,
                    state.rowsPerPage,
                    state.currentPage,
                    data?.data?.length
                  )}
                  paginationPerPage={state.rowsPerPage}
                  paginationRowsPerPageOptions={[5, 10, 20, 30]}
                  paginationComponentOptions={{
                    noRowsPerPage: false,
                    rangeSeparatorText: 'of',
                    selectAllRowsItem: state.isNext,
                    selectAllRowsItemText: 'All',
                    paginationButtonsDisabled: state.isNext
                  }}
                  onChangePage={handleChangePage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                  selectableRows={false}
                  noDataComponent={<EmptyData />}
                  className="table myDataTable table-hover align-middle mb-0 d-row nowrap dataTable no-footer dtr-inline"
                  highlightOnHover={true}
                  progressPending={isLoading || isFetching}
                  progressComponent={<TableSkeleton columns={8} />}
                />
              </div>
            </div>
          </TableCard>
        </div>
      </div>

      {/* View details modal */}
      <Modal
        centered
        dialogClassName="vd-modal"
        show={state.viewModal.status}
        onHide={() => setState((prev) => ({ ...prev, viewModal: { status: false, data: {} } }))}
      >
        <div className="vd-modal__hero">
          <button
            type="button"
            className="vd-modal__close"
            aria-label="Close"
            onClick={() =>
              setState((prev) => ({ ...prev, viewModal: { status: false, data: {} } }))
            }
          >
            <i className="icofont-close-line" aria-hidden="true" />
          </button>
          <span className="vd-modal__avatar">{initials(viewData.name)}</span>
          <div className="vd-modal__heading">
            <h4 className="vd-modal__name">{viewData.name || 'Visitor'}</h4>
            <div className="vd-modal__sub">
              <i className="icofont-building-alt" aria-hidden="true" />
              {viewData.company || 'No company provided'}
            </div>
            <span className={`vd-modal__status vd-modal__status--${viewData.status || 'Pending'}`}>
              {STATUS_LABEL[viewData.status] || viewData.status || '-'}
            </span>
          </div>
        </div>

        <div className="vd-modal__body">
          {/* Contact */}
          <div className="vd-modal__group">
            <p className="vd-modal__group-title">
              <i className="icofont-contact-add" aria-hidden="true" /> Contact
            </p>
            <div className="vd-modal__grid">
              <div className="vd-modal__item">
                <label>Mobile</label>
                <div className="vd-modal__item-value">
                  {viewData.phone ? <a href={`tel:${viewData.phone}`}>{viewData.phone}</a> : '-'}
                </div>
              </div>
              <div className="vd-modal__item">
                <label>Email</label>
                <div className="vd-modal__item-value">
                  {viewData.email ? <a href={`mailto:${viewData.email}`}>{viewData.email}</a> : '-'}
                </div>
              </div>
              <div className="vd-modal__item vd-modal__item--full">
                <label>Company / Organisation</label>
                <div className="vd-modal__item-value">{viewData.company || '-'}</div>
              </div>
            </div>
          </div>

          {/* Visit */}
          <div className="vd-modal__group">
            <p className="vd-modal__group-title">
              <i className="icofont-location-arrow" aria-hidden="true" /> Visit
            </p>
            <div className="vd-modal__grid">
              <div className="vd-modal__item">
                <label>Here to meet</label>
                <div className="vd-modal__item-value">{viewData.hostName || '-'}</div>
              </div>
              <div className="vd-modal__item">
                <label>Purpose</label>
                <div className="vd-modal__item-value">
                  {viewData.purpose ? (
                    <span className="vd-modal__pill">{viewData.purpose}</span>
                  ) : (
                    '-'
                  )}
                </div>
              </div>
              <div className="vd-modal__item">
                <label>Scheduled appointment</label>
                <div className="vd-modal__item-value">
                  <YesNoChip value={viewData.hasAppointment} />
                </div>
              </div>
              <div className="vd-modal__item">
                <label>Expected meeting time</label>
                <div className="vd-modal__item-value">{viewData.expectedMeetingTime || '-'}</div>
              </div>
              <div className="vd-modal__item">
                <label>Accessibility assistance</label>
                <div className="vd-modal__item-value">
                  <YesNoChip value={viewData.needsAccessibility} />
                </div>
              </div>
              {viewData.needsAccessibility ? (
                <div className="vd-modal__item vd-modal__item--full">
                  <label>Accessibility details</label>
                  <div className="vd-modal__item-value" style={{ whiteSpace: 'pre-wrap' }}>
                    {viewData.accessibilityDetails || '-'}
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {/* Timing */}
          <div className="vd-modal__group">
            <p className="vd-modal__group-title">
              <i className="icofont-clock-time" aria-hidden="true" /> Timing
            </p>
            <div className="vd-modal__grid">
              <div className="vd-modal__item vd-modal__item--full">
                <label>Checked in on</label>
                <div className="vd-modal__item-value">
                  {formatDate(viewData.createdAt, 'DD-MMM-YYYY hh:mm a')}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="vd-modal__footer">
          {viewData.status === VISIT_STATUS.PENDING && viewData._id ? (
            <button
              type="button"
              className="vd-modal__btn vd-modal__btn--primary"
              onClick={() => {
                handleStatusChange(viewData._id, VISIT_STATUS.CHECKED_IN);
                setState((prev) => ({ ...prev, viewModal: { status: false, data: {} } }));
              }}
            >
              <i className="icofont-check-circled" aria-hidden="true" />
              Mark Checked In
            </button>
          ) : null}
          {viewData.status === VISIT_STATUS.CHECKED_IN && viewData._id ? (
            <button
              type="button"
              className="vd-modal__btn vd-modal__btn--secondary"
              onClick={() => {
                handleStatusChange(viewData._id, VISIT_STATUS.CHECKED_OUT);
                setState((prev) => ({ ...prev, viewModal: { status: false, data: {} } }));
              }}
            >
              <i className="icofont-sign-out" aria-hidden="true" />
              Mark Checked Out
            </button>
          ) : null}
        </div>
      </Modal>

      {/* QR code modal */}
      <Modal
        centered
        className="guest-qr"
        dialogClassName="guest-qr__dialog"
        contentClassName="guest-qr__content"
        show={state.qrModal}
        onHide={() => setState((prev) => ({ ...prev, qrModal: false }))}
      >
        <Modal.Header className="guest-qr__header">
          <div className="guest-qr__header-content">
            <span className="guest-qr__header-icon" aria-hidden="true">
              <i className="icofont-qr-code" />
            </span>
            <div className="guest-qr__header-copy">
              <Modal.Title className="guest-qr__title">Guest Check-In QR code</Modal.Title>
              <p className="guest-qr__subtitle">
                Display or print this code at reception. Visitors scan it to open the check-in form.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn-close guest-qr__close"
            aria-label="Close"
            onClick={() => setState((prev) => ({ ...prev, qrModal: false }))}
          />
        </Modal.Header>
        <Modal.Body className="guest-qr__body">
          <div className="guest-qr__card">
            {state.qrDataUrl ? (
              <img className="guest-qr__image" src={state.qrDataUrl} alt="Guest Check-In QR code" />
            ) : (
              <div className="guest-qr__loading">
                <span className="spinner-border" role="status" />
              </div>
            )}
            <div className="guest-qr__link">
              <code className="guest-qr__url">{CHECKIN_FORM_URL}</code>
              <button type="button" className="guest-qr__copy" onClick={handleCopyLink}>
                <i className="icofont-copy" aria-hidden="true" />
                Copy
              </button>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="guest-qr__footer">
          <button
            type="button"
            className="guest-qr__download"
            onClick={handleDownloadQr}
            disabled={!state.qrDataUrl}
          >
            <i className="icofont-download" aria-hidden="true" />
            Download PNG
          </button>
        </Modal.Footer>
      </Modal>

      {/* Delete confirmation */}
      <ConfirmDialog
        show={state.deleteModal.status}
        onHide={() => setState((prev) => ({ ...prev, deleteModal: { status: false, id: '' } }))}
        onConfirm={handleDelete}
        loading={state.deleting}
        variant="danger"
        icon="icofont-ui-delete"
        title="Delete check-in?"
        message="Are you sure you want to delete this visitor record? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </div>
  );
};

export default ContactUsList;
